import { isDocEmpty, type TiptapDoc } from '@odo/shared-ui';
import {
    MAX_CUSTOM_TYPE_LENGTH,
    MAX_DESCRIPTION_NODES,
    MAX_TRIGGER_ALIGNER,
    MAX_TRIGGER_NOGOS,
    MAX_TRIGGER_WEEKS,
    MIN_TRIGGER_ALIGNER,
    MIN_TRIGGER_NOGOS,
    MIN_TRIGGER_WEEKS,
    VIRTUAL_APPOINTMENT_TEAMS,
    VIRTUAL_APPOINTMENT_TRIGGER_KINDS,
    VIRTUAL_APPOINTMENT_WEEKS_FROM,
    type VirtualAppointmentDraftPlan,
    type VirtualAppointmentPlan,
    type VirtualAppointmentTeam,
    type VirtualAppointmentTriggerKind,
    type VirtualAppointmentWeeksFrom
} from '../../services/virtualAppointments';
import { stripResolvedImageSrcs } from '../../utils/docImageResolution';
import {
    currentTreatmentWeek,
    DEFAULT_WEEKS_FROM,
    todayUtcIso,
    utcDayOf,
    weeksFromOf
} from './virtualAppointmentWeeks';
import { NO_DM_LINK_HINT } from './virtualAppointmentNogos';
import {
    parseAlignerInput,
    parseNogosInput,
    parseWeeksInput
} from './virtualAppointmentTriggerInput';
import {
    keptWeeksAnchor,
    nextSeriesWeeksFromError,
    NO_SAVED_TRIGGERS,
    triggerProgressError,
    type AlignerLimit,
    type ParsedTrigger,
    type SavedTriggers
} from './virtualAppointmentTriggerAhead';

export type VirtualAppointmentTypeMode = 'code' | 'custom';

export const DEFAULT_VIRTUAL_APPOINTMENT_TEAM: VirtualAppointmentTeam = 'DM';
export const DEFAULT_VIRTUAL_APPOINTMENT_TRIGGER: VirtualAppointmentTriggerKind = 'aligner';

// Editable form state for one VA. Inputs stay raw strings so a half-typed value survives
// re-renders; every trigger value is kept so toggling the kind never loses input.
export interface VirtualAppointmentDraft {
    key: string;
    id?: string;
    typeMode: VirtualAppointmentTypeMode;
    typeCode: string;
    typeCustom: string;
    /** 'now' only on an existing VA opened straight away, echoed back unchanged. */
    triggerKind: VirtualAppointmentTriggerKind;
    triggerAligner: string;
    triggerWeeks: string;
    weeksFrom: VirtualAppointmentWeeksFrom;
    triggerNogos: string;
    team: VirtualAppointmentTeam;
    /** '' for nobody. */
    assigneeEmail: string;
    descriptionDoc: TiptapDoc;
}

export type VirtualAppointmentDraftPatch = Partial<Omit<VirtualAppointmentDraft, 'key' | 'id'>>;

export interface VirtualAppointmentDraftErrors {
    type?: string;
    trigger?: string;
    description?: string;
}

function emptyDoc(): TiptapDoc {
    return { type: 'doc', content: [{ type: 'paragraph' }] };
}

let fallbackKeyCounter = 0;

export function newDraftKey(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return `new-${crypto.randomUUID()}`;
    }
    fallbackKeyCounter += 1;
    return `new-${Date.now()}-${fallbackKeyCounter}`;
}

export function createEmptyDraft(): VirtualAppointmentDraft {
    return {
        key: newDraftKey(),
        typeMode: 'code',
        typeCode: '',
        typeCustom: '',
        triggerKind: DEFAULT_VIRTUAL_APPOINTMENT_TRIGGER,
        triggerAligner: '',
        triggerWeeks: '',
        weeksFrom: DEFAULT_WEEKS_FROM,
        triggerNogos: '',
        team: DEFAULT_VIRTUAL_APPOINTMENT_TEAM,
        assigneeEmail: '',
        descriptionDoc: emptyDoc()
    };
}

const INTEGER_PATTERN = /^-?\d+$/;

// Stored rows (DTOs, submitted plans, draft rows) back into form state. A string custom type
// with no code means custom mode, so an emptied custom field survives a draft round-trip.
export function draftFromStored(source: VirtualAppointmentDraftPlan): VirtualAppointmentDraft {
    const isCustom = typeof source.typeCustom === 'string' && !source.typeCode;
    return {
        key: source.id ?? newDraftKey(),
        ...(source.id != null && { id: source.id }),
        typeMode: isCustom ? 'custom' : 'code',
        typeCode: isCustom ? '' : (source.typeCode ?? ''),
        typeCustom: source.typeCustom ?? '',
        triggerKind: source.triggerKind ?? DEFAULT_VIRTUAL_APPOINTMENT_TRIGGER,
        triggerAligner: source.triggerAligner != null ? String(source.triggerAligner) : '',
        triggerWeeks: source.triggerWeeks != null ? String(source.triggerWeeks) : '',
        weeksFrom: source.weeksFrom ?? weeksFromOf(source.triggerWeeksFrom ?? null),
        triggerNogos: source.triggerNogos != null ? String(source.triggerNogos) : '',
        team: source.team ?? DEFAULT_VIRTUAL_APPOINTMENT_TEAM,
        assigneeEmail: source.assigneeEmail ?? '',
        descriptionDoc: source.descriptionDoc ?? emptyDoc()
    };
}

function persistedDoc(doc: TiptapDoc): TiptapDoc | null {
    return isDocEmpty(doc) ? null : stripResolvedImageSrcs(doc);
}

function parseIntegerInput(raw: string): number | null {
    const trimmed = raw.trim();
    if (!INTEGER_PATTERN.test(trimmed)) return null;
    const value = Number(trimmed);
    return Number.isSafeInteger(value) ? value : null;
}

// Keeps a half-filled row as typed, including the inactive trigger value.
export function draftToDraftPlan(draft: VirtualAppointmentDraft): VirtualAppointmentDraftPlan {
    const isCustom = draft.typeMode === 'custom';
    return {
        ...(draft.id !== undefined && { id: draft.id }),
        typeCode: isCustom || draft.typeCode === '' ? null : draft.typeCode,
        typeCustom: isCustom ? draft.typeCustom : null,
        triggerKind: draft.triggerKind,
        triggerAligner: parseIntegerInput(draft.triggerAligner),
        triggerWeeks: parseIntegerInput(draft.triggerWeeks),
        weeksFrom: draft.weeksFrom,
        triggerNogos: parseIntegerInput(draft.triggerNogos),
        team: draft.team,
        assigneeEmail: draft.assigneeEmail || null,
        descriptionDoc: persistedDoc(draft.descriptionDoc)
    };
}

function validateType(draft: VirtualAppointmentDraft): string | undefined {
    if (draft.typeMode === 'code') {
        return draft.typeCode.length > 0 ? undefined : 'Choose a type';
    }
    const custom = draft.typeCustom.trim();
    if (custom.length === 0) return 'Enter a custom type';
    if (custom.length > MAX_CUSTOM_TYPE_LENGTH) {
        return `Keep the custom type under ${MAX_CUSTOM_TYPE_LENGTH} characters`;
    }
    return undefined;
}

export interface DraftTriggerContext {
    /** UTC day the patient's Treatment phase began; null when unknown or not in treatment. */
    treatmentStartDate: string | null;
    /** UTC today, YYYY-MM-DD. */
    todayIso: string;
    /** Whether the patient is linked to Dental Monitoring, which counts no-gos. */
    dmLinked: boolean;
    /** The patient's current aligner; null when unknown, which allows any aligner. */
    currentAligner: number | null;
    /** The highest aligner a trigger may name; null when the count is unknown. */
    alignerLimit: AlignerLimit | null;
    /** Set when a week counted from the treatment start could never fire: no start on record. */
    weeksUnavailable: boolean;
    /** False where a week must count from the treatment start, as on a refinement's next series. */
    weeksFromNowAllowed: boolean;
    /** Saved triggers by VA id; a saved VA is only checked against progress once its trigger changes. */
    savedTriggers: SavedTriggers;
}

export type TriggerProgressContext = Partial<
    Pick<
        DraftTriggerContext,
        | 'currentAligner'
        | 'alignerLimit'
        | 'weeksUnavailable'
        | 'weeksFromNowAllowed'
        | 'savedTriggers'
    >
>;

const NO_TRIGGER_CONTEXT: DraftTriggerContext = {
    treatmentStartDate: null,
    todayIso: '',
    dmLinked: true,
    currentAligner: null,
    alignerLimit: null,
    weeksUnavailable: false,
    weeksFromNowAllowed: true,
    savedTriggers: NO_SAVED_TRIGGERS
};

export function triggerContextFor(
    treatmentStartDate: string | null,
    dmLinked: boolean,
    progress: TriggerProgressContext = {}
): DraftTriggerContext {
    return {
        ...NO_TRIGGER_CONTEXT,
        ...progress,
        treatmentStartDate,
        todayIso: todayUtcIso(),
        dmLinked
    };
}

export function parsedTrigger(draft: VirtualAppointmentDraft): ParsedTrigger {
    return {
        ...(draft.id !== undefined && { id: draft.id }),
        triggerKind: draft.triggerKind,
        aligner: parseAlignerInput(draft.triggerAligner),
        weeks: parseWeeksInput(draft.triggerWeeks),
        weeksFrom: draft.weeksFrom
    };
}

// The day a "from now" week counts from: the saved anchor while kept, else today; null otherwise.
export function draftWeeksAnchorDay(
    draft: VirtualAppointmentDraft,
    savedTriggers: SavedTriggers,
    todayIso: string
): string | null {
    if (draft.triggerKind !== 'weeks' || draft.weeksFrom !== 'now') return null;
    return utcDayOf(keptWeeksAnchor(parsedTrigger(draft), savedTriggers)) ?? todayIso;
}

function progressError(
    draft: VirtualAppointmentDraft,
    context: DraftTriggerContext
): string | undefined {
    const progress = {
        currentAligner: context.currentAligner,
        currentWeek: currentTreatmentWeek(context.treatmentStartDate, context.todayIso),
        alignerLimit: context.alignerLimit,
        weeksUnavailable: context.weeksUnavailable
    };
    return triggerProgressError(parsedTrigger(draft), progress, context.savedTriggers);
}

function validateAligner(
    draft: VirtualAppointmentDraft,
    context: DraftTriggerContext
): string | undefined {
    if (parseAlignerInput(draft.triggerAligner) === null) {
        return `Enter an aligner number from ${MIN_TRIGGER_ALIGNER} to ${MAX_TRIGGER_ALIGNER}`;
    }
    return progressError(draft, context);
}

function validateWeeks(
    draft: VirtualAppointmentDraft,
    context: DraftTriggerContext
): string | undefined {
    if (parseWeeksInput(draft.triggerWeeks) === null) {
        return `Enter a week from ${MIN_TRIGGER_WEEKS} to ${MAX_TRIGGER_WEEKS}`;
    }
    return (
        nextSeriesWeeksFromError(
            parsedTrigger(draft),
            context.weeksFromNowAllowed,
            context.savedTriggers
        ) ?? progressError(draft, context)
    );
}

function validateNogos(
    draft: VirtualAppointmentDraft,
    context: DraftTriggerContext
): string | undefined {
    if (parseNogosInput(draft.triggerNogos) === null) {
        return `Enter a number of no-gos from ${MIN_TRIGGER_NOGOS} to ${MAX_TRIGGER_NOGOS}`;
    }
    // Only new VAs, as the API only refuses a newly written no-go trigger.
    return draft.id === undefined && !context.dmLinked ? NO_DM_LINK_HINT : undefined;
}

function validateTrigger(
    draft: VirtualAppointmentDraft,
    context: DraftTriggerContext
): string | undefined {
    switch (draft.triggerKind) {
        case 'aligner':
            return validateAligner(draft, context);
        case 'weeks':
            return validateWeeks(draft, context);
        case 'nogos':
            return validateNogos(draft, context);
        case 'now':
            return draft.id !== undefined ? undefined : 'Choose an aligner, week or no-go trigger';
    }
}

// Mirrors the backend's top-level node cap on stored Tiptap docs.
export function isDescriptionOverCap(doc: TiptapDoc): boolean {
    return doc.content.length > MAX_DESCRIPTION_NODES;
}

interface DocNode {
    type?: string;
    text?: string;
    attrs?: Record<string, unknown>;
    content?: DocNode[];
}

function isNonEmptyString(value: unknown): boolean {
    return typeof value === 'string' && value.length > 0;
}

function nodeHasContent(node: DocNode): boolean {
    if (typeof node.text === 'string' && node.text.trim().length > 0) return true;
    if (node.type === 'image' && isNonEmptyString(node.attrs?.['data-file-id'])) return true;
    if (node.type === 'imageGridCell' && isNonEmptyString(node.attrs?.fileId)) return true;
    return (node.content ?? []).some(nodeHasContent);
}

// Mirrors the backend submit rule: empty = no doc, or no non-whitespace text and no uploaded
// image (an image-only description is valid).
export function isDescriptionEmpty(doc: TiptapDoc | null | undefined): boolean {
    if (!doc || !Array.isArray(doc.content)) return true;
    return !(doc.content as DocNode[]).some(nodeHasContent);
}

function validateDescription(doc: TiptapDoc): string | undefined {
    if (isDescriptionOverCap(doc)) return 'This description is too long to save; shorten it';
    return isDescriptionEmpty(doc) ? 'Add a description' : undefined;
}

// Type and description only, for VAs that open straight away and carry no trigger.
export function validateDraftContent(
    draft: VirtualAppointmentDraft
): VirtualAppointmentDraftErrors {
    const type = validateType(draft);
    const description = validateDescription(draft.descriptionDoc);
    return {
        ...(type && { type }),
        ...(description && { description })
    };
}

// Without a context no aligner or week is out of reach and no DM link is required.
export function validateDraft(
    draft: VirtualAppointmentDraft,
    context: DraftTriggerContext = NO_TRIGGER_CONTEXT
): VirtualAppointmentDraftErrors {
    const trigger = validateTrigger(draft, context);
    return { ...validateDraftContent(draft), ...(trigger && { trigger }) };
}

export function hasDraftErrors(errors: VirtualAppointmentDraftErrors): boolean {
    return Boolean(errors.type ?? errors.trigger ?? errors.description);
}

export interface VirtualAppointmentPlanContent {
    typeCode: string | null;
    typeCustom: string | null;
    team: VirtualAppointmentTeam;
    assigneeEmail: string | null;
    descriptionDoc: TiptapDoc | null;
}

// Everything but the id and trigger; callers must validate the content first.
export function draftToPlanContent(draft: VirtualAppointmentDraft): VirtualAppointmentPlanContent {
    const isCustom = draft.typeMode === 'custom';
    return {
        typeCode: isCustom ? null : draft.typeCode,
        typeCustom: isCustom ? draft.typeCustom.trim() : null,
        team: draft.team,
        assigneeEmail: draft.assigneeEmail || null,
        descriptionDoc: persistedDoc(draft.descriptionDoc)
    };
}

// Callers must validate first: an invalid trigger would otherwise serialise as a bogus value.
export function draftToPlan(draft: VirtualAppointmentDraft): VirtualAppointmentPlan {
    const base = {
        ...(draft.id !== undefined && { id: draft.id }),
        ...draftToPlanContent(draft)
    };
    switch (draft.triggerKind) {
        case 'aligner':
            return {
                ...base,
                triggerKind: 'aligner',
                triggerAligner: parseAlignerInput(draft.triggerAligner) ?? MIN_TRIGGER_ALIGNER
            };
        case 'weeks':
            return {
                ...base,
                triggerKind: 'weeks',
                triggerWeeks: parseWeeksInput(draft.triggerWeeks) ?? MIN_TRIGGER_WEEKS,
                weeksFrom: draft.weeksFrom
            };
        case 'nogos':
            return {
                ...base,
                triggerKind: 'nogos',
                triggerNogos: parseNogosInput(draft.triggerNogos) ?? MIN_TRIGGER_NOGOS
            };
        case 'now':
            return { ...base, triggerKind: 'now' };
    }
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function stringOrNull(value: unknown): string | null {
    return typeof value === 'string' ? value : null;
}

function integerOrNull(value: unknown): number | null {
    return Number.isInteger(value) ? (value as number) : null;
}

function parseDoc(value: unknown): TiptapDoc | null {
    return isRecord(value) && value.type === 'doc' && Array.isArray(value.content)
        ? (value as unknown as TiptapDoc)
        : null;
}

function pickEnum<T extends string>(options: readonly T[], value: unknown): T | null {
    return options.find((option) => option === value) ?? null;
}

function parseDraftPlan(raw: unknown): VirtualAppointmentDraftPlan | null {
    if (!isRecord(raw)) return null;
    return {
        ...(typeof raw.id === 'string' && raw.id.length > 0 && { id: raw.id }),
        typeCode: stringOrNull(raw.typeCode),
        typeCustom: stringOrNull(raw.typeCustom),
        triggerKind: pickEnum(VIRTUAL_APPOINTMENT_TRIGGER_KINDS, raw.triggerKind),
        triggerWeeks: integerOrNull(raw.triggerWeeks),
        weeksFrom: pickEnum(VIRTUAL_APPOINTMENT_WEEKS_FROM, raw.weeksFrom),
        triggerAligner: integerOrNull(raw.triggerAligner),
        triggerNogos: integerOrNull(raw.triggerNogos),
        team: pickEnum(VIRTUAL_APPOINTMENT_TEAMS, raw.team),
        assigneeEmail: stringOrNull(raw.assigneeEmail),
        descriptionDoc: parseDoc(raw.descriptionDoc)
    };
}

// `undefined` when the draft never carried the field: absent must stay distinct from [].
export function parseVirtualAppointmentDraftPlans(
    raw: unknown
): VirtualAppointmentDraftPlan[] | undefined {
    if (!Array.isArray(raw)) return undefined;
    return raw.flatMap((entry) => {
        const plan = parseDraftPlan(entry);
        return plan === null ? [] : [plan];
    });
}

export function parseSeenIds(raw: unknown): string[] | undefined {
    if (!Array.isArray(raw)) return undefined;
    return raw.filter((id): id is string => typeof id === 'string' && id.length > 0);
}
